/**
 * v1 faction registry — the 6 human-civilisation Classical-Antiquity
 * factions locked at §6.1 #21 (2026-07-09, v0M1.1 + v0M1.3 amendments).
 *
 * v1 mode = FFA (free-for-all): each faction is on its own team. Teams
 * separate only conceptually; the sim assigns `Faction.teamId = id` so
 * two factions never share a team. Alliances / co-op teams land post-MVP.
 *
 * Colors are canonical per faction (no player override — v0M3.7 menu
 * design lock). Chosen for high perceptual separation across the
 * strategic map political-tint overlay; roughly historical / flag-derived
 * but not pantone-matched.
 */

export interface FactionDef {
  /** Canonical numeric id — used as both FactionId and Faction.teamId in v1 FFA mode. */
  readonly id: number;
  /** Display name (English, historic). Also the menu selector key. */
  readonly name: string;
  /** Border tint RGB — v0M3.6 default; player-override retired at menu design (Michael 2026-07-12). */
  readonly color: readonly [number, number, number];
  /** Historical short blurb — potential HUD / tooltip use. */
  readonly blurb: string;
}

const FACTIONS: readonly FactionDef[] = [
  { id: 1, name: 'Rome', color: [200, 40, 40], blurb: 'The Republic — legions of the Italian peninsula.' },
  { id: 2, name: 'Macedon', color: [80, 60, 160], blurb: "Alexander's successors — pike phalanx of the north Aegean." },
  { id: 3, name: 'Achaemenid Persia', color: [220, 180, 40], blurb: 'The Great King — cavalry and archers of the plateau.' },
  { id: 4, name: 'Carthage', color: [40, 100, 180], blurb: 'The mercantile empire — trireme navies and mercenary armies.' },
  { id: 5, name: 'Ptolemaic Egypt', color: [80, 180, 160], blurb: 'The Ptolemies — Nile-fed grain and Hellenistic elite units.' },
  { id: 6, name: 'Gallic Celts', color: [90, 140, 60], blurb: 'The tribal confederations — warband infantry and horse.' },
];

/** Return all 6 v1 factions in canonical order. */
export function listFactions(): readonly FactionDef[] {
  return FACTIONS;
}

/** Look up a faction by display name; throws on unknown name. */
export function getFactionByName(name: string): FactionDef {
  const f = FACTIONS.find((x) => x.name === name);
  if (!f) {
    throw new Error(`Unknown faction: ${name}. Known: ${FACTIONS.map((x) => x.name).join(', ')}`);
  }
  return f;
}

/** Look up a faction by canonical id (1-6); throws if out of range. */
export function getFactionById(id: number): FactionDef {
  const f = FACTIONS.find((x) => x.id === id);
  if (!f) {
    throw new Error(`Unknown faction id: ${id}. Valid ids: 1-6.`);
  }
  return f;
}
